import React, { useEffect, useRef, useState } from 'react';

interface PullToRefreshProps {
  /** Called when the user pulls past the threshold. May return a promise. */
  onRefresh: () => Promise<unknown> | void;
  /** Classes for the scroll container itself (must allow vertical scrolling). */
  className?: string;
  children: React.ReactNode;
  /** When true, the pull gesture is ignored (e.g. offline). */
  disabled?: boolean;
}

const THRESHOLD = 70; // px the user must pull to trigger a refresh
const MAX_PULL = 110; // px cap on the visual pull distance
const RESISTANCE = 0.5; // drag-to-pixel ratio so the pull feels rubbery
const HOLD = 48; // px the spinner row holds at while refreshing
const MIN_SPINNER_MS = 600; // keep the spinner visible at least this long

/**
 * Wraps a scrollable area and adds a native-feeling pull-to-refresh gesture.
 * Native browser pull-to-refresh does not exist inside this app (the body is
 * `overflow:hidden` for the app-shell layout) nor in the installed PWA, so we
 * implement it on the inner scroll container itself.
 */
export const PullToRefresh: React.FC<PullToRefreshProps> = ({
  onRefresh,
  className,
  children,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number | null>(null);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  const disabledRef = useRef(disabled);
  const mountedRef = useRef(true);
  const timeoutRef = useRef<number | null>(null);

  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [dragging, setDragging] = useState(false);

  // Keep refs in sync so the once-mounted listeners always see current props.
  useEffect(() => { onRefreshRef.current = onRefresh; }, [onRefresh]);
  useEffect(() => { disabledRef.current = disabled; }, [disabled]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const setPullDistance = (d: number) => {
      pullRef.current = d;
      setPull(d);
    };

    const triggerRefresh = async () => {
      refreshingRef.current = true;
      setRefreshing(true);
      setPullDistance(HOLD);
      const startedAt = Date.now();
      try {
        await Promise.resolve(onRefreshRef.current?.());
      } catch {
        // Swallow refresh errors so handleEnd's fire-and-forget call can't
        // surface as an unhandled rejection; the UI just stops the spinner.
      } finally {
        const remaining = Math.max(0, MIN_SPINNER_MS - (Date.now() - startedAt));
        timeoutRef.current = window.setTimeout(() => {
          if (!mountedRef.current) return;
          refreshingRef.current = false;
          setRefreshing(false);
          setPullDistance(0);
        }, remaining);
      }
    };

    const handleStart = (e: TouchEvent) => {
      if (disabledRef.current || refreshingRef.current) return;
      // Only arm the gesture when already at the very top of the scroll area.
      startY.current = el.scrollTop <= 0 ? e.touches[0].clientY : null;
    };

    const handleMove = (e: TouchEvent) => {
      if (startY.current === null || refreshingRef.current) return;
      if (disabledRef.current) {
        // Became disabled mid-gesture — release without taking over scroll.
        startY.current = null;
        setDragging(false);
        setPullDistance(0);
        return;
      }
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0 && el.scrollTop <= 0) {
        // Pulling down at the top: take over from native scroll.
        e.preventDefault();
        setDragging(true);
        setPullDistance(Math.min(delta * RESISTANCE, MAX_PULL));
      } else if (delta <= 0) {
        // User scrolled up/normally — release the gesture.
        startY.current = null;
        setDragging(false);
        setPullDistance(0);
      }
    };

    const handleEnd = () => {
      if (startY.current === null) return;
      startY.current = null;
      setDragging(false);
      if (!disabledRef.current && pullRef.current >= THRESHOLD) {
        void triggerRefresh();
      } else {
        setPullDistance(0);
      }
    };

    el.addEventListener('touchstart', handleStart, { passive: true });
    el.addEventListener('touchmove', handleMove, { passive: false });
    el.addEventListener('touchend', handleEnd);
    el.addEventListener('touchcancel', handleEnd);
    return () => {
      el.removeEventListener('touchstart', handleStart);
      el.removeEventListener('touchmove', handleMove);
      el.removeEventListener('touchend', handleEnd);
      el.removeEventListener('touchcancel', handleEnd);
      mountedRef.current = false;
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  const progress = Math.min(pull / THRESHOLD, 1);

  return (
    <div ref={containerRef} className={className}>
      <div
        className="flex shrink-0 items-center justify-center overflow-hidden"
        style={{
          height: pull,
          opacity: progress,
          transition: dragging ? 'none' : 'height 0.25s ease, opacity 0.25s ease',
        }}
        aria-hidden={pull === 0}
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#E79F29"
          strokeWidth="2.5"
          strokeLinecap="round"
          className={refreshing ? 'animate-spin' : ''}
          style={{ transform: refreshing ? undefined : `rotate(${progress * 270}deg)` }}
        >
          {/* near-full circle so the rotation/spin reads clearly */}
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
        </svg>
      </div>
      {children}
    </div>
  );
};
